import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { History } from 'history';
import { arrayMove } from '../../modules/array-move';
import { AllQuotesOffer, Offer, OfferPermutation, RateQuote } from '../../modules/rate-quote/types';
import { publishRateQuoteSuccess } from '../link-modal/slice';
import {
  defaultRateQuoteState,
  LockedRateQuoteFailedStatus,
  LockedRateQuoteSuccessStatus,
  RateQuoteState
} from './state';

export const rateQuoteSlice = createSlice({
  name: 'rateQuote',
  initialState: defaultRateQuoteState,
  reducers: {
    fetchRateQuoteRequest: (state, _: PayloadAction<{ rateQuoteId: string }>) => ({
      ...state,
      loading: true,
      error: null
    }),
    fetchRateQuoteSuccess: (state, { payload }: PayloadAction<RateQuote>) => ({
      ...state,
      loading: false,
      rateQuote: payload,
      error: null
    }),
    fetchRateQuoteError: (
      state,
      { payload }: PayloadAction<{ message: string; code: number }>
    ) => ({
      ...state,
      loading: false,
      error: `Error fetching rateQuote: ${payload.message}`
    }),
    createOfferRequest: (
      state,
      _: PayloadAction<{
        rateQuoteId: string;
        offer: Offer | AllQuotesOffer;
        copyPMIValue: boolean;
      }>
    ) => state,
    createOfferSuccess: (
      state,
      {
        payload: { offer, offerPermutations }
      }: PayloadAction<{ offer: Offer; offerPermutations: OfferPermutation[] }>
    ) => ({
      ...state,
      rateQuote: {
        ...state.rateQuote,
        offers: {
          ...(state.rateQuote || {}).offers,
          [offer.offerId]: offer
        },
        offerPermutations
      } as RateQuote
    }),
    createOfferError: (state, _: PayloadAction<string>) => state,
    reorderOffers: (
      state,
      { payload: { oldIndex, newIndex } }: PayloadAction<{ oldIndex: number; newIndex: number }>
    ) => {
      if (
        oldIndex !== newIndex &&
        state.rateQuote &&
        oldIndex >= 0 &&
        oldIndex < state.rateQuote.merchandising.length
      ) {
        const merchandising = arrayMove([...state.rateQuote.merchandising], oldIndex, newIndex);
        return {
          ...state,
          rateQuote: {
            ...state.rateQuote,
            merchandising
          }
        };
      }
      return state;
    },
    merchandiseOffer: (state, { payload }: PayloadAction<string>) => {
      if (state.rateQuote && state.rateQuote.merchandising.every((id) => id !== payload)) {
        return {
          ...state,
          rateQuote: {
            ...state.rateQuote,
            merchandising: [...state.rateQuote.merchandising, payload]
          } as RateQuote
        };
      }
      return state;
    },
    unmerchandiseOffer: (state, { payload: offerId }: PayloadAction<string>) => {
      if (state.rateQuote) {
        const newMerchandising = state.rateQuote.merchandising.filter((id) => id !== offerId);
        return {
          ...state,
          rateQuote: {
            ...state.rateQuote,
            merchandising: newMerchandising
          }
        };
      }
      return state;
    },
    updateMerchandising: (state, _: PayloadAction<string[]>) => state,
    synchronizeMerchandisingCache: (state) => state,
    clearMerchandisingFromCache: (state, _: PayloadAction<string>) => state,
    synchedMerchandisingFromCache: (state) => state,
    storeOffer: (
      state,
      {
        payload: { offerId, offerData }
      }: PayloadAction<{ offerId: string; offerData: Partial<Offer> }>
    ) => {
      const rateQuote = state.rateQuote;
      if (rateQuote) {
        const id = offerData.offerId || offerId;
        if (id) {
          const updatedOffer = rateQuote.offers[id];
          if (updatedOffer) {
            return {
              ...state,
              rateQuote: {
                ...state.rateQuote,
                offers: {
                  ...rateQuote.offers,
                  [id]: { ...updatedOffer, ...offerData }
                }
              }
            } as unknown as RateQuoteState;
          }
        }
      }
      return state;
    },
    setLockedOffer: (
      state,
      { payload: { offerId, locked } }: PayloadAction<{ offerId: string; locked: boolean }>
    ) => {
      if (state.rateQuote) {
        return {
          ...state,
          rateQuote: {
            ...state.rateQuote,
            lockedOfferId: locked ? offerId : null,
            offers: {
              ...state.rateQuote.offers,
              [offerId]: {
                ...state.rateQuote.offers[offerId],
                locked
              }
            }
          }
        } as unknown as RateQuoteState;
      }
      return state;
    },
    updateOffer: (
      state,
      _: PayloadAction<{
        rateQuoteId: string;
        offerData: Partial<Offer>;
        copyPMIValue: boolean;
      }>
    ) => state,
    updateOfferSuccess: (state) => state,
    updateOfferError: (state, _: PayloadAction<string>) => state,
    saveMerchandising: (state, { payload }: PayloadAction<boolean>) => ({
      ...state,
      saveQueued: !payload
    }),
    saveMerchandisingSuccess: (state, { payload }: PayloadAction<RateQuote>) => {
      if (state.rateQuote) {
        return {
          ...state,
          rateQuote: {
            ...state.rateQuote,
            lastModifiedTime: payload.lastModifiedTime
          }
        };
      }
      return state;
    },
    saveMerchandisingError: (state, _: PayloadAction<string>) => state,
    fetchLockedOfferBySource: (
      state,
      _: PayloadAction<{ sourceId: string; history: History<any> }>
    ) => ({
      ...state,
      lockedRateQuoteBySource: { status: 'pending' }
    }),
    fetchLockedOfferBySourceSuccess: (
      state,
      { payload }: PayloadAction<LockedRateQuoteSuccessStatus>
    ) => ({
      ...state,
      lockedRateQuoteBySource: payload
    }),
    fetchLockedOfferBySourceFailure: (
      state,
      { payload }: PayloadAction<LockedRateQuoteFailedStatus>
    ) => ({
      ...state,
      lockedRateQuoteBySource: payload
    })
  },
  extraReducers: (builder) => {
    builder.addCase(publishRateQuoteSuccess, (state, { payload }) => {
      if (state.rateQuote) {
        return {
          ...state,
          rateQuote: {
            ...state.rateQuote,
            status: payload.rateQuote.status
          }
        };
      }
      return state;
    });
  }
});

export const {
  fetchRateQuoteRequest,
  fetchRateQuoteSuccess,
  fetchRateQuoteError,
  createOfferRequest,
  createOfferSuccess,
  createOfferError,
  reorderOffers,
  merchandiseOffer,
  unmerchandiseOffer,
  updateMerchandising,
  synchronizeMerchandisingCache,
  clearMerchandisingFromCache,
  synchedMerchandisingFromCache,
  storeOffer,
  setLockedOffer,
  updateOffer,
  updateOfferSuccess,
  updateOfferError,
  saveMerchandising,
  saveMerchandisingSuccess,
  saveMerchandisingError,
  fetchLockedOfferBySource,
  fetchLockedOfferBySourceSuccess,
  fetchLockedOfferBySourceFailure
} = rateQuoteSlice.actions;

export const reducer = rateQuoteSlice.reducer;

// Export action types for saga compatibility
export const FETCH_RATE_QUOTE_ACTION_TYPE = 'rateQuote/fetchRateQuoteRequest';
export const FETCH_RATE_QUOTE_SUCCESS_ACTION_TYPE = 'rateQuote/fetchRateQuoteSuccess';
export const FETCH_RATE_QUOTE_FAILURE_ACTION_TYPE = 'rateQuote/fetchRateQuoteError';
export const CREATE_OFFER_ACTION_TYPE = 'rateQuote/createOfferRequest';
export const CREATE_OFFER_SUCCESS_ACTION_TYPE = 'rateQuote/createOfferSuccess';
export const CREATE_OFFER_FAILURE_ACTION_TYPE = 'rateQuote/createOfferError';
export const REORDER_OFFERS_ACTION_TYPE = 'rateQuote/reorderOffers';
export const MERCHANDISE_OFFER_ACTION_TYPE = 'rateQuote/merchandiseOffer';
export const UNMERCHANDISE_OFFER_ACTION_TYPE = 'rateQuote/unmerchandiseOffer';
export const UPDATE_MERCHANDISING_ACTION_TYPE = 'rateQuote/updateMerchandising';
export const SYNCHRONIZE_MERCHANDISING_CAHE_ACTION_TYPE = 'rateQuote/synchronizeMerchandisingCache';
export const CLEAR_MERCHANDISING_FROM_CACHE_ACTION_TYPE = 'rateQuote/clearMerchandisingFromCache';
export const SYNCHRONIZED_MERCHANDISING_FROM_CACHE_ACTION_TYPE =
  'rateQuote/synchedMerchandisingFromCache';
export const STORE_OFFER_ACTION_TYPE = 'rateQuote/storeOffer';
export const SET_LOCKED_OFFER_ACTION_TYPE = 'rateQuote/setLockedOffer';
export const UPDATE_OFFER_ACTION_TYPE = 'rateQuote/updateOffer';
export const UPDATE_OFFER_SUCCESS_ACTION_TYPE = 'rateQuote/updateOfferSuccess';
export const UPDATE_OFFER_FAILURE_ACTION_TYPE = 'rateQuote/updateOfferError';
export const SAVE_MERCHANDISING_ACTION_TYPE = 'rateQuote/saveMerchandising';
export const SAVE_MERCHANDISING_SUCCESS_ACTION_TYPE = 'rateQuote/saveMerchandisingSuccess';
export const SAVE_MERCHANDISING_ERROR_ACTION_TYPE = 'rateQuote/saveMerchandisingError';
export const FETCH_LOCKED_OFFER_BY_SOURCE_ACTION_TYPE = 'rateQuote/fetchLockedOfferBySource';
export const FETCH_LOCKED_OFFER_BY_SOURCE_SUCCESS_ACTION_TYPE =
  'rateQuote/fetchLockedOfferBySourceSuccess';
export const FETCH_LOCKED_OFFER_BY_SOURCE_FAILURE_ACTION_TYPE =
  'rateQuote/fetchLockedOfferBySourceFailure';

// Re-export types for compatibility
export type FetchRateQuoteRequestAction = ReturnType<typeof fetchRateQuoteRequest>;
export type FetchRateQuoteSuccessAction = ReturnType<typeof fetchRateQuoteSuccess>;
export type FetchRateQuoteFailureAction = ReturnType<typeof fetchRateQuoteError>;
export type CreateOfferRequestAction = ReturnType<typeof createOfferRequest>;
export type CreateOfferSuccessAction = ReturnType<typeof createOfferSuccess>;
export type CreateOfferFailureAction = ReturnType<typeof createOfferError>;
export type ReorderOffersAction = ReturnType<typeof reorderOffers>;
export type MerchandiseOfferAction = ReturnType<typeof merchandiseOffer>;
export type UnmerchandiseOfferAction = ReturnType<typeof unmerchandiseOffer>;
export type UpdateMerchandisingAction = ReturnType<typeof updateMerchandising>;
export type SynchronizeMerchandisingCacheAction = ReturnType<typeof synchronizeMerchandisingCache>;
export type ClearMerchandisingFromCacheAction = ReturnType<typeof clearMerchandisingFromCache>;
export type SynchronizedMerchandisingFromCacheAction = ReturnType<
  typeof synchedMerchandisingFromCache
>;
export type StoreOfferAction = ReturnType<typeof storeOffer>;
export type SetLockedOfferAction = ReturnType<typeof setLockedOffer>;
export type UpdateOfferAction = ReturnType<typeof updateOffer>;
export type UpdateOfferSuccessAction = ReturnType<typeof updateOfferSuccess>;
export type UpdateOfferFailureAction = ReturnType<typeof updateOfferError>;
export type SaveMerchandisingAction = ReturnType<typeof saveMerchandising>;
export type SaveMerchandisingSuccessAction = ReturnType<typeof saveMerchandisingSuccess>;
export type SaveMerchandisingErrorAction = ReturnType<typeof saveMerchandisingError>;
export type FetchLockedOfferBySourceAction = ReturnType<typeof fetchLockedOfferBySource>;
export type FetchLockedOfferBySourceSuccessAction = ReturnType<
  typeof fetchLockedOfferBySourceSuccess
>;
export type FetchLockedOfferBySourceFailureAction = ReturnType<
  typeof fetchLockedOfferBySourceFailure
>;
